import React from 'react';
import {
  Box, Button, IconButton, Paper, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type {
  HeaderStyle, IrrigationDetailPanelProps, IrrigationStatus, Row,
} from '../riego.types';
import {
  CHART_UI, CTA_ORANGE, DETAIL_HEADER_COLORS, RADIUS,
} from '../dashboardColors';
import {
  EM_DASH, MS_PER_MINUTE, SCHEDULED_STATUSES, STATUS_LABEL,
} from '../riego.constants';
import { pressableSx } from '../riegoGantt.styles';

const headerStyleFor = (status: IrrigationStatus): HeaderStyle => {
  if (status === 'proposal') {
    return { title: 'Riego Propuesto', color: DETAIL_HEADER_COLORS.proposal };
  }
  if (SCHEDULED_STATUSES.has(status)) {
    return { title: 'Riego Programado', color: DETAIL_HEADER_COLORS.scheduled };
  }
  return { title: 'Riego Real', color: DETAIL_HEADER_COLORS.real };
};

const fmtDateTime = (ts?: string): string => {
  if (!ts) return EM_DASH;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${String(d.getFullYear()).slice(-2)}`;
  const time = d.toLocaleTimeString('es-CL', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date}, ${time}`;
};

const fmtNumber = (value?: number): string => (
  value == null ? EM_DASH : value.toLocaleString('es-CL', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
);

const fmtDuration = (initTime?: string, endTime?: string): string => {
  if (!initTime || !endTime) return EM_DASH;
  const diff = new Date(endTime).getTime() - new Date(initTime).getTime();
  if (Number.isNaN(diff) || diff <= 0) return EM_DASH;
  return `${Math.round(diff / MS_PER_MINUTE)} min`;
};

const DetailCell: React.FC<Row> = ({ label, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    gap={2}
    sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
  >
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{value}</Typography>
  </Stack>
);

const IrrigationDetailPanel: React.FC<IrrigationDetailPanelProps> = ({
  irrigation, zoneName, onClose, onDelete, onStop, onActivate, onVerCaudal,
  canDelete, canStop, canActivate, isDeleting, isStopping, isActivating,
}) => {
  const header = headerStyleFor(irrigation.status);
  const estado = irrigation.rawStatus || STATUS_LABEL[irrigation.status] || irrigation.status;

  const leftRows: Row[] = [
    { label: 'Inicio', value: fmtDateTime(irrigation.initTime) },
    { label: 'Duración', value: fmtDuration(irrigation.initTime, irrigation.endTime) },
    { label: 'Estado', value: estado },
    { label: 'Volumen [mm]', value: fmtNumber(irrigation.precipitation?.value) },
    { label: 'Fertirriego', value: EM_DASH },
  ];

  const rightRows: Row[] = [
    { label: 'Fin', value: fmtDateTime(irrigation.endTime) },
    { label: 'Tipo de Riego', value: irrigation.irrigationType || EM_DASH },
    { label: 'Enviado a la Red', value: EM_DASH },
    { label: 'Caudal Teórico [m³/h]', value: EM_DASH },
    { label: 'Control pH', value: EM_DASH },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: RADIUS.card,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        boxShadow: CHART_UI.paperShadow,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          bgcolor: header.color, color: '#fff', px: 2.5, py: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          {header.title}
          {' · '}
          {zoneName}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="cerrar" sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5, pt: 1, pb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 5,
          }}
        >
          <Box>{leftRows.map((r) => <DetailCell key={r.label} {...r} />)}</Box>
          <Box>{rightRows.map((r) => <DetailCell key={r.label} {...r} />)}</Box>
        </Box>

        <Stack direction="row" gap={1.5} sx={{ mt: 2, flexWrap: 'wrap' }}>
          {canStop && onStop && (
            <Button
              variant="contained"
              onClick={onStop}
              disabled={isStopping}
              sx={{
                ...pressableSx,
                bgcolor: CTA_ORANGE.main,
                color: CTA_ORANGE.contrastText,
                '&:hover': { bgcolor: CTA_ORANGE.hover },
              }}
            >
              {isStopping ? 'Deteniendo…' : 'Detener riego'}
            </Button>
          )}
          {canActivate && onActivate && (
            <Button
              variant="contained"
              color="success"
              onClick={onActivate}
              disabled={isActivating}
              sx={pressableSx}
            >
              {isActivating ? 'Reanudando…' : 'Reanudar riego'}
            </Button>
          )}
          {canDelete && onDelete && (
            <Button
              variant="outlined"
              color="error"
              onClick={onDelete}
              disabled={isDeleting}
              sx={pressableSx}
            >
              {isDeleting ? 'Eliminando…' : 'Eliminar programación'}
            </Button>
          )}
          {onVerCaudal && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onVerCaudal}
              sx={{ ...pressableSx, '&:hover': { borderColor: CTA_ORANGE.main } }}
            >
              Ver caudal
            </Button>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default IrrigationDetailPanel;
